import { ArrowRight, Plus, Trash2 } from "lucide-react";
import Select from "react-select";
import type { TransformEntry } from "../facet-form-config";
import {
    FACET_SELECT_EMPTY_VALUE_LABEL,
    getFacetSelectStyles,
    toFacetSelectOptions,
    type FacetSelectOption,
} from "./facet-select-styles";

interface NormalizeMappingEditorProps {
    entries: TransformEntry[];
    keyValueOptions: string[];
    partyOptions: string[];
    partyPlaceholder: string;
    onChange: (entries: TransformEntry[]) => void;
    optionsRefreshKey?: number;
}

function mergeSelectOptions(value: string, options: string[]): FacetSelectOption[] {
    const selectOptions = toFacetSelectOptions(options);
    const trimmed = value.trim();
    if (trimmed && !options.includes(trimmed)) {
        return [{ value: trimmed, label: trimmed }, ...selectOptions];
    }
    return selectOptions;
}

export default function NormalizeMappingEditor({
    entries,
    keyValueOptions,
    partyOptions,
    partyPlaceholder,
    onChange,
    optionsRefreshKey = 0,
}: NormalizeMappingEditorProps) {
    const update = (index: number, field: "key" | "value", val: string) => {
        onChange(entries.map((e, i) => (i === index ? { ...e, [field]: val } : e)));
    };

    const addRow = () => onChange([...entries, { key: "", value: "" }]);

    const removeRow = (index: number) => {
        const next = entries.filter((_, i) => i !== index);
        onChange(next.length > 0 ? next : [{ key: "", value: "" }]);
    };

    return (
        <div className="flex flex-col gap-2 rounded-lg border border-emerald-200/90 bg-emerald-50/50 p-3">
            <div className="hidden grid-cols-[1fr_16px_1fr_32px] items-center gap-2 px-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 sm:grid">
                <span>Key Value</span>
                <span />
                <span>Party Name</span>
                <span />
            </div>
            {entries.map((entry, i) => {
                const keyLabel = entry.key === "" ? FACET_SELECT_EMPTY_VALUE_LABEL : entry.key;
                const keySelectValue = entry.key === "" ? null : { value: entry.key, label: keyLabel };
                const partyLabel = entry.value === "" ? partyPlaceholder : entry.value;
                const partySelectValue = entry.value === "" ? null : { value: entry.value, label: partyLabel };

                return (
                    <div
                        key={i}
                        className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[1fr_16px_1fr_32px]"
                    >
                        <Select<FacetSelectOption, false>
                            key={`norm-key-${optionsRefreshKey}-${i}`}
                            isClearable={false}
                            options={mergeSelectOptions(entry.key, keyValueOptions)}
                            value={keySelectValue}
                            placeholder="Select key value"
                            classNamePrefix="normalize-key-select"
                            styles={getFacetSelectStyles(false)}
                            onChange={(opt) => update(i, "key", opt?.value ?? "")}
                        />
                        <ArrowRight size={14} className="hidden shrink-0 text-slate-300 sm:block" />
                        <Select<FacetSelectOption, false>
                            key={`norm-party-${optionsRefreshKey}-${i}-${partyOptions.join("|")}`}
                            isClearable={false}
                            options={mergeSelectOptions(entry.value, partyOptions)}
                            value={partySelectValue}
                            placeholder={partyPlaceholder}
                            classNamePrefix="normalize-party-select"
                            styles={getFacetSelectStyles(false)}
                            onChange={(opt) => update(i, "value", opt?.value ?? "")}
                        />
                        <button
                            type="button"
                            onClick={() => removeRow(i)}
                            className="shrink-0 justify-self-center rounded-md p-1.5 text-slate-400 transition hover:bg-red-50 hover:text-red-500"
                        >
                            <Trash2 size={14} />
                        </button>
                    </div>
                );
            })}
            <button
                type="button"
                onClick={addRow}
                className="mt-1 flex w-fit items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-semibold text-emerald-700 transition hover:bg-emerald-100"
            >
                <Plus size={13} /> Add mapping
            </button>
        </div>
    );
}
